// Send message form.
import React, { Suspense } from "react";
import { FormattedMessage, defineMessages, injectIntl } from "react-intl";
import { Drafty, Tinode } from "../tinode/tinode";
import MessageContent from '../tinode/message-content'

// Lazy-loading AudioRecorder because it's quite large due to
// a dependency on webm-duration-fix.
const AudioRecorder = React.lazy((_) => import("./audio-recorder.jsx"));

import { KEYPRESS_DELAY } from "../config.js";
import { replyFormatter } from "../lib/formatters.js";
import Sticker from "./sticker.jsx";
import ShareContacts from './share-contacts.jsx'
import {
  IconAttachFile,
  IconContact,
  IconEmoji,
  IconReply,
  IconImage,
  IconSend,
  IconVote,
  IconClose,
} from "../icons/svg";
import Helpers from "../lib/helpers";
import * as MC_CONST from '../tinode/constants'
import lodash from 'lodash'
import TagMessage from "./tag-message.jsx";
import { profile } from "../stores/storages/ProfileStorage";
import StringUtils from '../lib/string'
import { Topic } from '../tinode/topic'
import { MAX_EXTERN_ATTACHMENT_SIZE } from '../config'
import { bytesToHumanSize } from '../lib/strformat'
import AccessMode from "../tinode/access-mode";

const messages = defineMessages({
  messaging_disabled: {
    id: "messaging_disabled_prompt",
    defaultMessage: "Messaging disabled",
    description: "Prompt in SendMessage in read-only topic",
  },
  type_new_message: {
    id: "new_message_prompt",
    defaultMessage: "New message",
    description: "Prompt in send message field",
  },
  add_image_caption: {
    id: "image_caption_prompt",
    defaultMessage: "Image caption",
    description: "Prompt in SendMessage for attached image",
  },
  file_attachment_too_large: {
    id: "file_attachment_too_large",
    defaultMessage: "The file size {size} exceeds the {limit} limit.",
    description: "Error message when attachment is too large",
  },
  cannot_initiate_upload: {
    id: "cannot_initiate_file_upload",
    defaultMessage: "Cannot initiate file upload.",
    description: "Generic error messagewhen attachment fails",
  },
  icon_title_record_voice: {
    id: "icon_title_record_voice",
    defaultMessage: "Record voice message",
    description: "Icon tool tip for recording a voice message",
  },
  icon_title_attach_file: {
    id: "icon_title_attach_file",
    defaultMessage: "Attach file",
    description: "Icon tool tip for attaching a file",
  },
  icon_title_add_image: {
    id: "icon_title_add_image",
    defaultMessage: "Add image",
    description: "Icon tool tip for attaching an image",
  },
  icon_title_send: {
    id: "icon_title_send",
    defaultMessage: "Send message",
    description: "Icon tool tip for sending a message",
  },
  icon_title_contact: {
    id: 'icon_title_contact',
    defaultMessage: 'Send contact',
    description: 'Icon tool tip for sending a message'
  },
  icon_title_create_vote: {
    id: 'icon_title_create_vote',
    defaultMessage: 'Send contact',
    description: 'Icon tool tip for sending a message'
  },
  tag_all_label: {
    id: 'tag_all_label',
    defaultMessage: 'All',
    description: 'Lable tag all'
  }
});

class SendMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      quote: null,
      message: "",
      audioRec: false,
      audioAvailable: false,
      showSticker: false,
      showShareContact: false,
      showContactTag: false,
    };

    this.topicTagAll = {
      acs: new AccessMode({
        mode: "JRWPAS",
      }),
      public: {
        photo: {
          ref: MC_CONST.TAG_ALL.AVATAR,
        }
      },
      private: {
        comment: props.intl.formatMessage(messages.tag_all_label)
      },
      topic: MC_CONST.TAG_ALL.MEMBER_ID,
      user: MC_CONST.TAG_ALL.MEMBER_ID,
    }

    this.sendMessageByEnterEvent = {}

    // Timestamp when the previous key press was sent to the server.
    this.keypressTimestamp = 0;

    this.addEventListener = this.addEventListener.bind(this);
    this.removeEventListener = this.removeEventListener.bind(this);


    this.handlePasteEvent = this.handlePasteEvent.bind(this);
    this.handleAttachImage = this.handleAttachImage.bind(this);
    this.handleAttachFile = this.handleAttachFile.bind(this);
    this.handleAttachAudio = this.handleAttachAudio.bind(this);
    this.handleSend = this.handleSend.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleMessageTyping = this.handleMessageTyping.bind(this);
    this.handleDropAttach = this.handleDropAttach.bind(this);

    this.handleQuoteClick = this.handleQuoteClick.bind(this);

    this.formatReply = this.formatReply.bind(this);
    this.validateSendMessage = this.validateSendMessage.bind(this);

    this.handleShowOrHiddenStickerClick = this.handleShowOrHiddenStickerClick.bind(this);
    this.handleOnStickerClose = this.handleOnStickerClose.bind(this);
    this.handleOnSendSticker = this.handleOnSendSticker.bind(this);
    this.toggleStickerWindow = this.toggleStickerWindow.bind(this);
    this.setMessageContent = this.setMessageContent.bind(this);
    this.setBlankMentionTag = this.setBlankMentionTag.bind(this);

    this.handleOnSendContact = this.handleOnSendContact.bind(this);
    this.handleOnShareContactClose = this.handleOnShareContactClose.bind(this);

    this.handleOnHideContactTag = this.handleOnHideContactTag.bind(this);
    this.handleOnAddContactTag = this.handleOnAddContactTag.bind(this);
    this.handleUpdateContactTag = this.handleUpdateContactTag.bind(this);
    this.handleCreateVote = this.handleCreateVote.bind(this);
  }

  componentDidMount() {
    if (this.messageEditArea) {
      this.addEventListener()
      if (
        window
          .getComputedStyle(this.messageEditArea)
          .getPropertyValue("transition-property") == "all"
      ) {
        // Set focus on desktop, but not on mobile: focus causes soft keyboard to pop up.
        this.messageEditArea.focus();
      }
    }

    this.messageEditArea.focus()

    this.setState({ quote: this.formatReply() });

    this.setBlankMentionTag()
    if (!!this.props.topic.getDraft) {
      this.setState({
        message: (!!this.props.topic.getDraft ? this.props.topic.getDraft() : false) || ""
      });
    }

  }

  componentWillUnmount() {
    const { message } = this.state;
    const { topic } = this.props;
    topic.setDraft(message);
    this.removeEventListener()
  }

  componentDidUpdate(prevProps) {
    if (this.messageEditArea) {
      this.addEventListener()
      if (
        window
          .getComputedStyle(this.messageEditArea)
          .getPropertyValue("transition-property") == "all"
      ) {
        // Set focus on desktop, but not on mobile: focus causes soft keyboard to pop up.
        this.messageEditArea.focus();
      }

      // Adjust height of the message area for the amount of text.
      this.messageEditArea.style.height = "0px";
      this.messageEditArea.style.height =
        this.messageEditArea.scrollHeight + "px";
    }

    if (prevProps.topicName != this.props.topicName) {
      const { message } = this.state;
      prevProps.topic.setDraft(message);
      this.setState({
        message: this.props.initMessage || (!!this.props.topic.getDraft ? this.props.topic.getDraft() : false) || "",
        audioRec: false,
        quote: null,
        showMentionList: false
      });
      if (this.messageEditArea) {
        this.messageEditArea.focus()
      }
    } else if (prevProps.initMessage != this.props.initMessage) {
      const msg = this.props.initMessage || "";
      this.setState({ message: msg }, (_) => {
        // If there is text, scroll to bottom and set caret to the end.
        this.messageEditArea.scrollTop = this.messageEditArea.scrollHeight;
        this.messageEditArea.setSelectionRange(msg.length, msg.length);
      });
    }
    if (prevProps.reply != this.props.reply) {
      this.setState({ quote: this.formatReply() });
    }
  }

  addEventListener() {
    if (this.messageEditArea) {
      this.messageEditArea.addEventListener(
        "paste",
        this.handlePasteEvent,
        false
      );
    }
  }

  removeEventListener() {
    if (this.messageEditArea) {
      this.messageEditArea.removeEventListener(
        "paste",
        this.handlePasteEvent,
        false
      );
    }
  }

  setMessageContent(message) {
    const { topic } = this.props;
    this.setState({ message });
  }

  setBlankMentionTag() {
    this.setState({
      mentionStartAt: -1,
      mentionSize: 0,
      mentionList: [],
      showMentionList: false,
      currentSelectedMention: null,
      mentionTags: null,
      mentionTagAll: false,
    })
  }

  setChatInputRef(ref) {
    this.messageEditArea = ref
    this.props.tinode.messageEditArea = ref
  }

  formatReply() {
    const quote = this.props.reply
      ? Drafty.format(this.props.reply.content, replyFormatter, {
        formatMessage: this.props.intl.formatMessage.bind(this.props.intl),
        authorizeURL: this.props.tinode.authorizeURL.bind(this.props.tinode),
      })
      : null;

    if (quote) {
      const children = quote.props.children
      const {
        tags = [],
        tagAll = false,
      } = this.props.reply
      if ((tags && tags.length > 0) || (children[2] && children[2][0] && typeof (children[2][0]) === 'string')) {
        children[2][0] = MessageContent.renderContent(this.props.tinode, children[2][0], { tags, clickAction: false, tagAll })
      }
    }

    return quote
  }

  handlePasteEvent(e) {
    if (this.props.disabled) {
      return;
    }
    // FIXME: handle large files too.
    if (e.clipboardData && e.clipboardData.types && e.clipboardData.types.includes('Files')) {
      const files = e.clipboardData.files

      const listImgFile = []
      const listFile = []
      const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);
      Array.prototype.forEach.call(files, (file) => {
        if (file && file.size <= maxExternAttachmentSize) {
          if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
            const textClipboard = e.clipboardData.getData('text')
            if (textClipboard !== '') {
              // Gửi ảnh Excel
              const data = {
                onSendText: () => this.props.onSendMessage(textClipboard.trim()),
                onSendImage: () => this.props.onAttachImage([file]),
                imageExcel: file,
                imageExcelText: textClipboard,
              }
              this.props.onShowDialog(Tinode.DIALOG_TYPE.SEND_EXCEL_TEXT, data);

            } else {
              listImgFile.push(file)
            }
          } else {
            listFile.push(file)
          }
        } else {
          Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_too_large,
            { size: bytesToHumanSize(file.size), limit: bytesToHumanSize(maxExternAttachmentSize) }))
        }
      })

      if (listImgFile.length > 0) {
        this.props.onAttachImage(listImgFile);
      }
      if (listFile.length > 0) {
        this.props.onAttachFile(listFile);
      }

      e.preventDefault();
    }
  }

  handleAttachImage(e) {
    const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);
    if (e.target.files && e.target.files.length > 0) {
      const files = e.target.files
      const listFilesToSend = []
      Array.prototype.forEach.call(files, (file) => {
        if (file && file.size <= maxExternAttachmentSize) {
          listFilesToSend.push(file)
        } else {
          Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_too_large,
            { size: bytesToHumanSize(file.size), limit: bytesToHumanSize(maxExternAttachmentSize) }))
        }
      })

      this.props.onAttachImage(listFilesToSend);
    }
    // Clear the value so the same file can be uploaded again.
    e.target.value = "";
  }

  handleAttachFile(e) {
    const maxExternAttachmentSize = this.props.tinode.getServerParam('maxFileUploadSize', MAX_EXTERN_ATTACHMENT_SIZE);
    if (e.target.files && e.target.files.length > 0) {
      const files = e.target.files
      const listFilesToSend = []

      Array.prototype.forEach.call(files, (file) => {
        if (file && file.size <= maxExternAttachmentSize) {
          listFilesToSend.push(file)
        } else {
          Helpers.showErrorToasts(this.props.intl.formatMessage(messages.file_attachment_too_large,
            { size: bytesToHumanSize(file.size), limit: bytesToHumanSize(maxExternAttachmentSize) }))
        }
      })
      this.props.onAttachFile(listFilesToSend);
    }
    // Clear the value so the same file can be uploaded again.
    e.target.value = "";
  }

  handleDropAttach(files) {
    if (files && files.length > 0) {
      this.props.onAttachFile(files);
    }
  }

  handleAttachAudio(url, preview, duration) {
    this.setState({ audioRec: false });
    this.props.onAttachAudio(url, preview, duration);
  }

  handleOnSendSticker(selectedCollection, sticker) {
    const collectionPrefix = selectedCollection.prefix
    const img = sticker.img
    const url = `${collectionPrefix}/${img}`;
    const audio = `${collectionPrefix}/${sticker.voice}`;
    const stickerMessage = Drafty.insertSticker(null, 0, {
      mime: Helpers.getMimeType(img),
      refurl: url,
      width: 150,
      height: 150,
      audio
    })
    stickerMessage.subtype = MC_CONST.NODE_SUBTYPE_MESSAGE.VOICE_STICKER
    stickerMessage.collectionId = selectedCollection.collectionId
    stickerMessage.stickerId = sticker.stickerId
    stickerMessage.stikerType = sticker.type
    try {
      const urlAudio = Helpers.getDownloadURL(audio);
      if (urlAudio.toLowerCase().endsWith('.mp3')) {
        const audioPlay = new Audio(urlAudio);
        audioPlay.play();
      }
    } catch (error) {
      console.error('handleOnSendSticker', error);
    }

    this.props.onSendMessage(stickerMessage)
    this.handleOnStickerClose()
  }

  handleOnSendContact(topic, phone, name) {
    const avatar = lodash.has(topic, 'public.photo.ref') ? topic.public.photo.ref : null
    const contactMessage = Drafty.insertContact(null, 0, {
      phone,
      name,
      topic: topic.topic,
      avatar,
    })

    contactMessage.subtype = MC_CONST.NODE_SUBTYPE_MESSAGE.CONTACT
    contactMessage.sharedPhone = phone
    contactMessage.sharedContactName = name

    this.props.onSendMessage(contactMessage)
  }

  handleSend(e) {
    e.preventDefault();
    const message = this.state.message.trim();
    let content = message

    if (this.validateSendMessage(e)) {
      if (Array.isArray(this.state.mentionTags)) {
        let mentionTags = []

        this.state.mentionTags.forEach((tag) => {
          const newTagName = `@${tag.msisdn}`
          if (content.includes(tag.tag_name)) {
            mentionTags.push(tag)
          }
          content = content.replace(tag.tag_name, newTagName)
          tag.tag_name = newTagName
        })
        const msg = {
          subtype: MC_CONST.NODE_SUBTYPE_MESSAGE.TEXT,
          content: content,
          tags: mentionTags || [],
          tagAll: this.state.mentionTagAll,
        }
        this.props.onSendMessage(msg)
        this.setBlankMentionTag()
      } else {
        this.props.onSendMessage(message);
      }


    }

    this.setState({
      message: "",
    }, () => {
      const { topic } = this.props;
      topic.setDraft("");
    });
    this.handleOnStickerClose()
    this.handleOnHideContactTag()

  }

  validateSendMessage(e) {
    if (e && e.timeStamp) {
      if (this.sendMessageByEnterEvent[e.timeStamp] == null) {
        this.sendMessageByEnterEvent[e.timeStamp] = 1

        setTimeout(() => {
          this.sendMessageByEnterEvent[e.timeStamp] = null
        }, 2000)
      } else {
        return false;
      }
    }
    const message = this.state.message.trim();
    return message || this.props.acceptBlank || this.props.noInput;
  }

  /* Send on Enter key */
  handleKeyPress(e) {
    if (this.state.audioRec) {
      // Ignore key presses while audio is being recorded.
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    //nếu đang hiển thị tag thành viên thì key enter, key down, key up không hoạt động
    if (this.state.showMentionList && (e.keyCode === 38 || e.key === 'ArrowUp' || e.keyCode === 40 || e.key === 'ArrowDown' || e.keyCode === 13 || e.key === 'Enter')) {
      e.preventDefault();
      return
    }
    // Remove this if you don't want Enter to trigger send
    if (e.key === "Enter") {
      // Have Shift-Enter insert a line break instead
      if (!e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();

        this.handleSend(e);
      }

      if (e.altKey) {
        const newValue = this.messageEditArea.value + '\r\n'
        this.setMessageContent(newValue)
        this.messageEditArea.focus()
      }
    }
  }

  handleKeyUp(e) {
    if (this.props.isGroup) {
      this.handleUpdateContactTag()
    }
  }

  handleUpdateContactTag() {
    const chatInputElement = this.messageEditArea

    if (!!!chatInputElement) {
      return
    }

    if (chatInputElement.value.trim() === '') {
      this.handleOnHideContactTag()
      return
    }

    const inputText = String(chatInputElement.value.substring(0, chatInputElement.selectionStart))
    let start = -1
    if (inputText.includes('@')) {
      start = inputText.lastIndexOf('@') + 1
      this.setState({ mentionStartAt: start })
    }

    if (start > 0) {
      const listMemberInGroup = [this.topicTagAll].concat(this.props.groupMembers)
      const startCharacter = chatInputElement.value.substring(start - 1, start)
      let filteredData = listMemberInGroup
      if (start !== -1) {
        const mention = chatInputElement.value.substring(start, chatInputElement.selectionStart)
        if (startCharacter === '@') {
          if (mention !== '') {
            if (mention.trim() === '') {
              filteredData = []
            } else {
              const listMember = listMemberInGroup
                .filter((d) => {
                  if (d.jid === profile.username) return false
                  const dataStr = (lodash.has(d, 'public.fn') ? d.public.fn : '').concat(lodash.has(d, 'private.comment') ? d.private.comment : '').concat(d.jid ? d.jid : '')
                  return StringUtils.removeAccents(dataStr).toLowerCase().includes(StringUtils.removeAccents(mention).toLowerCase())
                }
                )
              filteredData = []
              const flags = []
              listMember.forEach((m) => {
                if (!flags[m.jid]) {
                  flags[m.jid] = true
                  filteredData.push(m)
                }
              })
            }
          } else {
            const listMember = listMemberInGroup.filter((d) => {
              if (d.jid === profile.username) return false
              return true
            })
            filteredData = []
            const flags = []
            listMember.forEach((m) => {
              if (!flags[m.jid]) {
                flags[m.jid] = true
                filteredData.push(m)
              }
            })
          }
        } else {
          filteredData = []
        }
      } else {
        filteredData = []
      }

      this.setState({ mentionList: filteredData })
      this.setState({ showMentionList: filteredData.length > 0 })
    }
  }

  handleOnHideContactTag() {
    this.setState({ mentionStartAt: -1, showMentionList: false, mentionList: [], mentionSize: 0, currentSelectedMsisdn: null })
  }

  handleOnAddContactTag(memberId) {
    let member
    let name
    let topic

    if (memberId === MC_CONST.TAG_ALL.MEMBER_ID) {
      member = memberId
      topic = this.topicTagAll
      this.setState({ mentionTagAll: true })
    } else {
      member = Topic.getThreadId(memberId)
      topic = this.props.tinode.getTopicByName(memberId)
    }

    name = memberId
    if (topic) {
      if (lodash.has(topic, 'private.comment')) {
        name = topic.private.comment
      } else if (lodash.has(topic, 'public.fn')) {
        name = topic.public.fn
      }
    }

    const mentionTag = {
      msisdn: member,
      name,
      tag_name: `@${name}`,
    }
    const mentionTags = this.state.mentionTags || []
    mentionTags.push(mentionTag)
    this.setState({ mentionTags })


    const firstValuePart = this.messageEditArea.value.slice(0, this.state.mentionStartAt - 1)
    const lastValuePart = this.messageEditArea.value.substring(this.messageEditArea.selectionStart)
    const newValue = `${firstValuePart}@${name} ${lastValuePart}`
    this.setMessageContent(newValue)
    this.messageEditArea.focus()
  }

  handleMessageTyping(e) {
    this.setMessageContent(e.target.value)
    if (this.props.onKeyPress) {
      const now = new Date().getTime();
      if (now - this.keypressTimestamp > KEYPRESS_DELAY) {
        this.props.onKeyPress();
        this.keypressTimestamp = now;
      }
    }
  }

  handleQuoteClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.props.reply && this.props.onQuoteClick) {
      const replyToSeq = this.props.reply.seq;
      this.props.onQuoteClick(replyToSeq);
    }
  }

  handleShowOrHiddenStickerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState((old) => {
      return {
        ...old,
        showSticker: !old.showSticker
      }
    });
  }

  handleOnStickerClose() {
    this.setState({
      showSticker: false,
    });
  }

  toggleStickerWindow = () => {
    this.setState({
      showSticker: !this.state.showSticker,
    })
  }

  handleOnShareContactShow = () => {
    this.setState({
      showShareContact: true,
    });
  }

  handleOnShareContactClose = () => {
    this.setState({
      showShareContact: false,
    });
  }

  handleCreateVote(e) {
    e.preventDefault();
    this.props.onShowDialog(Tinode.DIALOG_TYPE.CREATE_POLL);
  }

  render() {
    const { formatMessage } = this.props.intl;
    const prompt = this.props.disabled
      ? formatMessage(messages.messaging_disabled)
      : this.props.messagePrompt
        ? formatMessage(messages[this.props.messagePrompt])
        : formatMessage(messages.type_new_message);

    const sendIcon =
      this.props.reply && this.props.reply.editing ? "check_circle" : "send";
    const quote = this.state.quote ? (
      <div id="reply-quote-preview">
        <div className="icon">
          <IconReply />
        </div>

        {this.state.quote}
        <div className="cancel">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              this.props.onCancelReply();
            }}
          >
            <IconClose />
          </a>
        </div>
      </div>
    ) : null;
    const audioEnabled = this.state.audioAvailable && this.props.onAttachAudio;
    return (
      <div id="send-message-wrapper">
        {/* {!this.props.typingIndicator &&
          <div class="typing">
            <span className="username">
              {this.props.title}
            </span> <FormattedMessage
              id="typing"
              defaultMessage={"typing"}
            />
            <span class="jumping-dots">
              <span class="dot-1"></span>
              <span class="dot-2"></span>
              <span class="dot-3"></span>
            </span>
          </div>
        } */}
        {this.state.showSticker && <Sticker
          handleOnSendSticker={this.handleOnSendSticker}
          handleOnClose={this.handleOnStickerClose}
          setMessageContent={this.setMessageContent}
          message={this.state.message}
          messageEditArea={this.messageEditArea}
        />}
        {
          this.state.showShareContact &&
          <ShareContacts
            onClose={this.handleOnShareContactClose}
            tinode={this.props.tinode}
            topicName={this.props.topicName}
            contacts={this.props.contacts}
            handleOnSendContact={this.handleOnSendContact}
          />
        }
        {!this.props.noInput ? quote : null}
        {this.state.showMentionList && <TagMessage
          tinode={this.props.tinode}
          groupMembers={this.state.mentionList}
          myUserId={this.props.myUserId}
          onClose={this.handleOnHideContactTag}
          addTag={this.handleOnAddContactTag}
        />
        }
        <div id="send-message-panel">
          {!this.props.disabled ? (
            <>
              {this.props.onAttachFile && !this.state.audioRec ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    this.attachFile.click();
                  }}
                  className={"tooltip-container"}
                >
                  <IconAttachFile />
                  <div className="tooltip tooltip-file">
                    <FormattedMessage
                      id="icon_title_attach_file"
                      defaultMessage="Attach file"
                    />
                  </div>
                </a>
              ) : null}
              {this.props.noInput ? (
                quote || <div className="hr thin" />
              ) : this.state.audioRec ? (
                <Suspense
                  fallback={
                    <div>
                      <FormattedMessage
                        id="loading_note"
                        defaultMessage="Loading..."
                        description="Message shown when component is loading"
                      />
                    </div>
                  }
                >
                  <AudioRecorder
                    onRecordingProgress={(_) => this.props.onKeyPress(true)}
                    onDeleted={(_) => this.setState({ audioRec: false })}
                    onFinished={this.handleAttachAudio}
                  />
                </Suspense>
              ) : (
                <div className="gr-send-message-input">
                  <textarea
                    id="send-message-input"
                    placeholder={prompt}
                    value={this.state.message}
                    onChange={this.handleMessageTyping}
                    onKeyDown={this.handleKeyPress}
                    onKeyUp={this.handleKeyUp}
                    ref={(ref) => { this.setChatInputRef(ref) }}
                  />
                  <div className={`icon ${this.state.showSticker && 'active'}`} onClick={this.handleShowOrHiddenStickerClick}>
                    <div className="tooltip-container">
                      <IconEmoji />
                      <div className="tooltip tooltip-sticker">
                        <FormattedMessage
                          id="stickers"
                          defaultMessage="Stickers"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {this.props.isGroup &&
                <a href="#" onClick={this.handleCreateVote} >
                  <div className="tooltip-container">
                    <IconVote />
                    <div className="tooltip tooltip-contact">
                      <FormattedMessage
                        id="icon_title_create_vote"
                        defaultMessage="Vote"
                      />
                    </div>
                  </div>
                </a>
              }
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  this.handleOnShareContactShow();
                }}
              >
                <div className="tooltip-container">
                  <IconContact />
                  <div className="tooltip tooltip-contact">
                    <FormattedMessage
                      id="icon_title_contact"
                      defaultMessage="Send contact"
                    />
                  </div>
                </div>
              </a>
              {this.props.onAttachFile && !this.state.audioRec ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    this.attachImage.click();
                  }}
                >
                  <div className="tooltip-container">
                    <IconImage />
                    <div className="tooltip tooltip-contact">
                      <FormattedMessage
                        id="icon_title_add_image"
                        defaultMessage="Add image"
                      />
                    </div>
                  </div>

                </a>
              ) : null}
              {this.state.message || !audioEnabled ? (
                <a
                  href="#"
                  onClick={this.handleSend}
                >
                  <div className="icon" disabled={!this.validateSendMessage()}>
                    <div className="tooltip-container">
                      <IconSend />
                      <div className="tooltip tooltip-contact">
                        <FormattedMessage
                          id="icon_title_send"
                          defaultMessage="Send"
                        />
                      </div>
                    </div>
                  </div>
                </a>
              ) : !this.state.audioRec ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    this.setState({ audioRec: true });
                  }}
                >
                  <i className="material-icons">mic</i>
                </a>
              ) : null}
              <input
                type="file"
                multiple
                ref={(ref) => {
                  this.attachFile = ref;
                }}
                onChange={this.handleAttachFile}
                style={{ display: "none" }}
              />
              <input
                type="file"
                multiple
                ref={(ref) => {
                  this.attachImage = ref;
                }}
                accept="image/*, video/*"
                onChange={this.handleAttachImage}
                style={{ display: "none" }}
              />
            </>
          ) : (
            <div id="writing-disabled">{prompt}</div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(SendMessage);
